import React, { useEffect, useMemo } from 'react';
import orderBy from 'lodash/orderBy';
import { useApi } from '@axiom/ui';
import { CandidateOpportunitiesConst } from '@axiom/const';
import type { Opportunity } from '@axiom/validation';
import { useParams } from 'react-router';

import { SubmissionsApi } from '../../api/submissions-api';
import { OpportunityLegacyApi } from '../../api/opportunity-legacy-api';
import { OpportunityUtil } from '../../utils/opportunity-util';

import { OpportunitySubmissionContent } from './OpportunitySubmissionContent';

const { CandidateStatuses } = CandidateOpportunitiesConst;

const SUBMISSION_CANDIDATE_STATUS_BLACKLIST = new Set([
  CandidateStatuses.Removed,
  CandidateStatuses.TalentOptOut,
]);

export type OpportunitySubmissionProps = {
  opportunity: Opportunity;
  name?: string;
};

export const OpportunitySubmission = ({
  opportunity,
  name = 'OPPORTUNITY_SUBMISSION',
}: OpportunitySubmissionProps) => {
  const { viewId } = useParams<{ viewId?: string }>();
  const { submissionId, id: opportunityId } = opportunity;

  const [{ data: submissionData }, { data: opportunityCandidatesData }] =
    useApi(
      SubmissionsApi.readSubmission(submissionId),
      OpportunityLegacyApi.readOpportunityCandidates(opportunityId)
    );

  /**
   * We need to always pull fresh data when we load this component
   */
  useEffect(() => {
    void SubmissionsApi.refreshSubmission(submissionId);
    void OpportunityLegacyApi.refreshOpportunityCandidates(opportunityId);
  }, []);

  const candidates = useMemo(() => {
    const filteredCandidates = opportunityCandidatesData.filter(
      candidate =>
        !SUBMISSION_CANDIDATE_STATUS_BLACKLIST.has(candidate.candidateStatus) &&
        candidate.position
    );

    const rankedCandidates = orderBy(
      filteredCandidates.filter(c => c.isActiveForSubmission),
      'submissionRank'
    );
    const unrankedCandidates = orderBy(
      filteredCandidates.filter(c => !c.isActiveForSubmission),
      'firstName'
    );

    return [...rankedCandidates, ...unrankedCandidates];
  }, [opportunityCandidatesData]);

  const readOnly = OpportunityUtil.isClosed(opportunity);

  return (
    <div data-test={name}>
      <OpportunitySubmissionContent
        name="OPPORTUNITY_SUBMISSION_CONTENT"
        submission={submissionData}
        candidates={candidates}
        candidateReputationClarificationPatchCall={[]}
        opportunity={opportunity}
        readOnly={readOnly}
        initialCandidateId={viewId}
      />
    </div>
  );
};
